import { Task, DbTask } from '@/types/entities/index';
import { Database } from '@/types/supabase';

// Типы для вставки и обновления задач в БД
type DbTaskInsert = Database['public']['Tables']['tasks']['Insert'];
type DbTaskUpdate = Database['public']['Tables']['tasks']['Update'];

/**
 * Маппер из БД в frontend (snake_case -> camelCase)
 */
export function mapDbTaskToTask(dbTask: DbTask): Task {
  return {
    id: dbTask.id,
    userId: dbTask.user_id,
    createdAt: new Date(dbTask.created_at),
    updatedAt: new Date(dbTask.updated_at),
    title: dbTask.title,
    description: dbTask.description || null,
    status: dbTask.status,
    type: dbTask.type || 'task',
    category: dbTask.category || null,
    deadline: dbTask.deadline || null,
    reminderAt: dbTask.reminder_at || null,
    startDate: dbTask.start_date || null,
    endDate: dbTask.end_date || null,
    repeatType: dbTask.repeat_type || null,
    completed: dbTask.completed || false,
    notification: dbTask.notification || false,
    relatedOrderId: dbTask.related_order_id || null,
    relatedInventoryId: dbTask.related_inventory_id || null,
    relatedClientId: dbTask.related_client_id || null,
  };
}

/**
 * Маппер из frontend в БД для создания (camelCase -> snake_case)
 */
export function mapTaskToDbInsert(task: Partial<Task>): DbTaskInsert {
  return {
    user_id: task.userId!,
    title: task.title!,
    description: task.description || null,
    status: task.status || 'pending',
    priority: 'medium', // Значение по умолчанию
    type: task.type || 'task',
    category: task.category || null,
    due_date: task.deadline ? new Date(task.deadline).toISOString() : null,
    reminder_at: task.reminderAt ? new Date(task.reminderAt).toISOString() : null,
    start_date: task.startDate ? new Date(task.startDate).toISOString() : null,
    end_date: task.endDate ? new Date(task.endDate).toISOString() : null,
    repeat_type: task.repeatType || null,
    completed: task.completed || false,
    notification: task.notification || false,
    related_order_id: task.relatedOrderId || null,
    related_inventory_id: task.relatedInventoryId || null,
    related_client_id: task.relatedClientId || null,
  };
}

/**
 * Маппер из frontend в БД для обновления (camelCase -> snake_case)
 */
export function mapTaskUpdateToDbUpdate(task: Partial<Task>): DbTaskUpdate {
  const update: DbTaskUpdate = {
    updated_at: new Date().toISOString(),
  };

  // Обновляем только переданные поля
  if (task.title !== undefined) update.title = task.title;
  if (task.description !== undefined) update.description = task.description;
  if (task.status !== undefined) update.status = task.status;
  if (task.type !== undefined) update.type = task.type;
  if (task.category !== undefined) update.category = task.category;
  if (task.deadline !== undefined) {
    update.due_date = task.deadline ? new Date(task.deadline).toISOString() : null;
  }
  if (task.reminderAt !== undefined) {
    update.reminder_at = task.reminderAt ? new Date(task.reminderAt).toISOString() : null;
  }
  if (task.startDate !== undefined) {
    update.start_date = task.startDate ? new Date(task.startDate).toISOString() : null;
  }
  if (task.endDate !== undefined) {
    update.end_date = task.endDate ? new Date(task.endDate).toISOString() : null;
  }
  if (task.repeatType !== undefined) update.repeat_type = task.repeatType;
  if (task.completed !== undefined) {
    update.completed = task.completed;
    // Если задача завершена, устанавливаем completed_at
    if (task.completed) {
      update.completed_at = new Date().toISOString();
    } else {
      update.completed_at = null;
    }
  }
  if (task.notification !== undefined) update.notification = task.notification;
  if (task.relatedOrderId !== undefined) update.related_order_id = task.relatedOrderId;
  if (task.relatedInventoryId !== undefined) update.related_inventory_id = task.relatedInventoryId;
  if (task.relatedClientId !== undefined) update.related_client_id = task.relatedClientId;

  return update;
}

/**
 * Допустимые статусы задач (должны соответствовать check constraint в БД)
 */
export const ALLOWED_TASK_STATUSES = [
  'pending',
  'in_progress', 
  'completed',
  'cancelled',
  'overdue'
] as const;

/**
 * Допустимые типы задач
 */
export const ALLOWED_TASK_TYPES = [
  'task',
  'reminder'
] as const;

/**
 * Допустимые приоритеты задач
 */
export const ALLOWED_TASK_PRIORITIES = [
  'low',
  'medium',
  'high', 
  'urgent'
] as const;
